import { BaseService } from './BaseService';
import { 
  ValidationError, 
  BusinessRuleError,
  ComplianceError 
} from '../utils/errors';
import { 
  ComplianceRule,
  ComplianceCheck,
  RegulatoryRequirement,
  FilingRequirement,
  ComplianceAlert,
  ComplianceRuleType
} from '../types/compliance';
import { Decimal } from 'decimal.js';
import { PoolClient } from 'pg';

interface ComplianceContext {
  fiscalSponsorId: string;
  causeId?: string;
  transactionId?: string;
  checkType: 'transaction' | 'periodic' | 'filing' | 'audit';
  metadata?: Record<string, any>;
}

// Add severity to ComplianceCheck interface if it's missing
interface ExtendedComplianceCheck extends ComplianceCheck {
  severity?: string;
  error?: string;
}

export class ComplianceMonitoringService extends BaseService {
  async monitorCompliance(
    context: ComplianceContext,
    data: any
  ): Promise<ComplianceCheck[]> {
    return await this.withTransaction(async (client) => {
      // Get applicable compliance rules
      const rules = await this.getApplicableRules(
        client,
        context
      );

      const checks: ComplianceCheck[] = [];
      const violations: ComplianceAlert[] = [];

      // Perform compliance checks
      for (const rule of rules) {
        const checkResult = await this.performComplianceCheck(
          client,
          rule,
          data,
          context
        );

        checks.push(checkResult);

        if (!checkResult.compliant) {
          violations.push(await this.createComplianceAlert(
            client,
            checkResult as ExtendedComplianceCheck,
            context
          ));
        }
      }

      // Check IRS requirements
      await this.checkIRSRequirements(client, context, data);

      // Check state requirements
      await this.checkStateRequirements(client, context, data);

      // Check Uniform Guidance requirements
      await this.checkUniformGuidance(client, context, data);

      // Create compliance record
      await this.createComplianceRecord(
        client,
        checks,
        violations,
        context
      );

      return checks;
    });
  }

  async checkFilingRequirements(
    fiscalSponsorId: string
  ): Promise<FilingRequirement[]> {
    const query = `
      WITH fiscal_year_data AS (
        SELECT 
          fs.id,
          fs.ein,
          fs.fiscal_year_end,
          COALESCE(SUM(CASE 
            WHEN je.debit > 0 THEN je.debit 
            ELSE je.credit 
          END), 0) as total_revenue,
          COUNT(DISTINCT g.id) as federal_grant_count,
          EXISTS(
            SELECT 1 
            FROM sponsfi.grants g2 
            WHERE g2.fiscal_sponsor_id = fs.id 
            AND g2.amount >= 750000
          ) as has_large_grants
        FROM sponsfi.fiscal_sponsors fs
        LEFT JOIN sponsfi.journal_entries je ON je.fiscal_sponsor_id = fs.id
        LEFT JOIN sponsfi.grants g ON g.fiscal_sponsor_id = fs.id
        WHERE fs.id = $1
        AND je.entry_date >= NOW() - INTERVAL '1 year'
        GROUP BY fs.id, fs.ein, fs.fiscal_year_end
      )
      SELECT 
        fyd.*,
        json_agg(DISTINCT sr.*) as state_requirements,
        json_agg(DISTINCT fr.*) as federal_requirements
      FROM fiscal_year_data fyd
      LEFT JOIN sponsfi.state_requirements sr ON true
      LEFT JOIN sponsfi.federal_requirements fr ON true
      GROUP BY fyd.id, fyd.ein, fyd.fiscal_year_end, 
               fyd.total_revenue, fyd.federal_grant_count, 
               fyd.has_large_grants
    `;

    return await this.withTransaction(async (client) => {
      const result = await client.query(query, [fiscalSponsorId]);
      return this.determineFilingRequirements(result.rows[0]);
    });
  }

  private async getApplicableRules(
    client: any,
    context: ComplianceContext
  ): Promise<ComplianceRule[]> {
    // Implementation
    return [];
  }

  private async determineFilingRequirements(data: any): Promise<FilingRequirement[]> {
    // Implementation
    return [];
  }

  private async performComplianceCheck(
    client: any,
    rule: ComplianceRule,
    data: any,
    context: ComplianceContext
  ): Promise<ComplianceCheck> {
    const check: ComplianceCheck = {
      ruleId: rule.id,
      checkType: context.checkType as any, // Type assertion to handle the type mismatch
      timestamp: new Date(),
      compliant: true,
      details: {}
    };

    try {
      switch (rule.type) {
        case 'transaction_limit':
          check.compliant = await this.checkTransactionLimit(
            client,
            rule,
            data
          );
          break;

        case 'fund_restriction':
          check.compliant = await this.checkFundRestriction(
            client,
            rule,
            data
          );
          break;

        case 'regulatory_requirement':
          check.compliant = await this.checkRegulatoryRequirement(
            client,
            rule,
            data
          );
          break;

        case 'filing_deadline':
          check.compliant = await this.checkFilingDeadline(
            client,
            rule,
            data
          );
          break;

        case 'audit_requirement':
          check.compliant = await this.checkAuditRequirement(
            client,
            rule,
            data
          );
          break;
      }

      check.details = await this.generateComplianceDetails(
        client,
        rule,
        data,
        check.compliant
      );

    } catch (error: any) { // Type annotation to handle the unknown error type
      check.compliant = false;
      (check as ExtendedComplianceCheck).error = error.message;
    }

    return check;
  }

  private async createComplianceAlert(
    dbClient: any,
    check: ExtendedComplianceCheck,
    context: ComplianceContext
  ): Promise<ComplianceAlert> {
    const query = `
      INSERT INTO sponsfi.compliance_alerts (
        fiscal_sponsor_id,
        cause_id,
        rule_id,
        check_type,
        severity,
        message,
        details,
        metadata,
        status
      ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, 'open')
      RETURNING *
    `;

    return await this.withTransaction(async (txClient) => {
      // Use the transaction client if available, otherwise use the provided client
      const client = txClient || dbClient;
      
      const result = await client.query(query, [
        context.fiscalSponsorId,
        context.causeId,
        check.ruleId,
        check.checkType,
        check.severity || 'high',
        this.generateAlertMessage(check),
        JSON.stringify(check.details),
        JSON.stringify({
          ...context.metadata,
          transaction_id: context.transactionId
        })
      ]);

      // Trigger notifications if needed
      await this.triggerComplianceNotifications(
        client,
        result.rows[0]
      );

      return result.rows[0];
    });
  }

  private async checkUniformGuidance(
    client: any,
    context: ComplianceContext,
    data: any
  ): Promise<void> {
    // Check procurement standards
    await this.checkProcurementStandards(client, context, data);

    // Check cost principles
    await this.checkCostPrinciples(client, context, data);

    // Check internal controls
    await this.checkInternalControls(client, context, data);

    // Check subrecipient monitoring
    await this.checkSubrecipientMonitoring(client, context, data);
  }

  // Implement missing methods
  private async checkTransactionLimit(client: any, rule: ComplianceRule, data: any): Promise<boolean> {
    // Implementation
    return true;
  }

  private async checkFundRestriction(client: any, rule: ComplianceRule, data: any): Promise<boolean> {
    // Implementation
    return true;
  }

  private async checkRegulatoryRequirement(client: any, rule: ComplianceRule, data: any): Promise<boolean> {
    // Implementation
    return true;
  }

  private async checkFilingDeadline(client: any, rule: ComplianceRule, data: any): Promise<boolean> {
    // Implementation
    return true;
  }

  private async checkAuditRequirement(client: any, rule: ComplianceRule, data: any): Promise<boolean> {
    // Implementation
    return true;
  }

  private async generateComplianceDetails(
    client: any, 
    rule: ComplianceRule, 
    data: any, 
    isCompliant: boolean
  ): Promise<Record<string, any>> {
    // Implementation
    return {};
  }

  private generateAlertMessage(check: ExtendedComplianceCheck): string {
    // Implementation
    return `Compliance check failed: ${check.ruleId}`;
  }

  private async triggerComplianceNotifications(client: any, alert: any): Promise<void> {
    // Implementation
  }

  private async createComplianceRecord(
    client: any,
    checks: ComplianceCheck[],
    violations: ComplianceAlert[],
    context: ComplianceContext
  ): Promise<void> {
    // Implementation
  }

  private async checkIRSRequirements(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }

  private async checkStateRequirements(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }

  private async checkProcurementStandards(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }

  private async checkCostPrinciples(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }

  private async checkInternalControls(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }

  private async checkSubrecipientMonitoring(client: any, context: ComplianceContext, data: any): Promise<void> {
    // Implementation
  }
} 